import type { RecipeV2Doc } from '@ctcells/api'
import { t, type MessageKey } from '@ctcells/i18n'

/**
 * 「＋ 레시피」 — 서버 표준 레시피(새 형식)를 불러온 채로 시작하는 새 레시피의 판정(DOM 없음).
 * eric 2026-10-12 「+레시피를 클릭하면.. 표준 레시피로 시작.. 빈 레시피 처럼 나오되.. 표준 레시피의 값들이 임포트 한 상태에서 보이게」.
 *
 * 값의 자리는 문서의 편집층 `designerApplication.data.process.<장비>.defaults` 다(서버 `recipe_v2` 의 공정 틀).
 * 🔴 **받은 문서 위에 고친 칸만 얹는다**(CLAUDE.md PUT 절과 같은 성질) — 모르는 칸 · 스텝 · 명령은 그대로 실려 간다.
 * 🔴 칸을 고치면 작업 명령이 낡는다 — 저장 전에 서버가 다시 만든다(`POST /recipes/regenerate` · 컨테이너). 화면은 명령을 짓지 않는다.
 */

type Obj = Record<string, unknown>

const isObj = (v: unknown): v is Obj => typeof v === 'object' && v !== null && !Array.isArray(v)

export type StandardModule = 'centrifuge' | 'assembler' | 'staining'

/** 고칠 수 있는 칸 하나 — `process.<module>.defaults.<key>` */
export interface StandardField {
  module: StandardModule
  key: string
  label: MessageKey
  kind: 'int' | 'text'
  /**
   * 칸이 서는 자리 — 없으면 장비 기본값 줄(그림 옆), `stages` 면 작업 차례 표 바로 위(그 표의 빈 「액체 클래스」에 쓰이는 값이라 ·
   * eric 2026-10-20 「위치가 애매한데. 어디가 나을까?」 → 「응 해봐」)
   */
  place?: 'stages'
  /**
   * `false` 면 레시피 추가 창에 입력 칸을 세우지 않는다 — 상세(읽기)의 한 줄은 남고 문서 값은 손대지 않은 채 그대로 실려 간다
   * (`docWithValues` 는 바뀐 칸만 얹는다)
   */
  input?: false
}

/**
 * 탭마다 칸. 이름은 빈 레시피 편집기(`recipeDraft.ts` 의 `FieldSpec`)와 같은 낱말을 쓴다 — 같은 뜻, 같은 글자.
 * 🔴 문서에 없는 칸은 화면에 세우지 않는다(`fieldsOf` 가 거른다) — 지어내지 않는다.
 */
export const STANDARD_FIELDS: readonly StandardField[] = [
  { module: 'centrifuge', key: 'speed_rpm', label: 'recipeEdit.f.speedRpm', kind: 'int' },
  { module: 'centrifuge', key: 'seconds', label: 'recipeEdit.f.durationSec', kind: 'int' },
  /* 어셈블러 차례 — UV 시간 · UV 각도 · 적재 기울기 · 배출 기울기(eric 2026-10-20 「uv 시간, uv 각도.. 적재 기울기. 배출 기울기.. 순으로」) */
  { module: 'assembler', key: 'uv_seconds', label: 'recipeEdit.f.uvDurationSec', kind: 'int' },
  { module: 'assembler', key: 'uv_tilt_angle_deg', label: 'recipeEdit.f.uvTilt', kind: 'int' },
  { module: 'assembler', key: 'load_tilt_angle_deg', label: 'recipeEdit.f.loadTilt', kind: 'int' },
  { module: 'assembler', key: 'drain_angle_deg', label: 'recipeEdit.f.drainAngle', kind: 'int' },
  /* 기본 팁 정책(`default_new_tip`)은 칸을 세우지 않는다 — 표준 레시피 액체 스텝마다 `newTip` 이 있어 안 쓰인다(eric 2026-10-20 「다 안쓰지?」 · 문서 값은 그대로 실려 간다) */
  {
    module: 'staining',
    key: 'default_liquid_class',
    label: 'recipeEdit.f.defaultLiquid',
    kind: 'text',
    place: 'stages',
    /* 입력 칸은 걷었다(eric 2026-10-21 「기본 액체 클래스 입력 창 없애줘.」) — 서버가 스텝에 액체 클래스가 없을 때 쓰는 기본값이라 문서 값은 그대로 · 작업 차례 표의 「water (기본)」도 그대로 */
    input: false,
  },
  {
    module: 'centrifuge',
    key: 'max_allowed_delay_sec',
    label: 'recipeEdit.f.maxDelay',
    kind: 'int',
  },
  {
    module: 'assembler',
    key: 'max_allowed_delay_sec',
    label: 'recipeEdit.f.maxDelay',
    kind: 'int',
  },
  {
    module: 'staining',
    key: 'max_allowed_delay_sec',
    label: 'recipeEdit.f.maxDelay',
    kind: 'int',
  },
]

export const fieldId = (f: StandardField) => `${f.module}.${f.key}`

function processOf(doc: RecipeV2Doc | undefined): Obj | undefined {
  const da = doc?.['designerApplication']
  const data = isObj(da) ? da['data'] : undefined
  const process = isObj(data) ? data['process'] : undefined
  return isObj(process) ? process : undefined
}

function defaultsOf(doc: RecipeV2Doc | undefined, module: StandardModule): Obj | undefined {
  const mod = processOf(doc)?.[module]
  const defaults = isObj(mod) ? mod['defaults'] : undefined
  return isObj(defaults) ? defaults : undefined
}

/** 문서에 실제로 있는 칸만(스칼라 값) — 탭마다 */
export function fieldsOf(
  doc: RecipeV2Doc | undefined,
  module: StandardModule,
  place?: 'stages',
): StandardField[] {
  const defaults = defaultsOf(doc, module)
  if (defaults === undefined) return []
  return STANDARD_FIELDS.filter((f) => {
    if (f.module !== module || f.place !== place) return false
    const v = defaults[f.key]
    return typeof v === 'number' || typeof v === 'string'
  })
}

/** 그 장비 기본값의 문서 값(글자) — 작업 차례 표의 빈 「액체 클래스」를 「water (기본)」로 채울 때 */
export function defaultValue(
  doc: RecipeV2Doc | undefined,
  module: StandardModule,
  key: string,
): string | undefined {
  const v = defaultsOf(doc, module)?.[key]
  return typeof v === 'string' || typeof v === 'number' ? String(v) : undefined
}

/** 칸의 글자 — 문서의 값 그대로(장비 기본값 + 작업 차례 · 시약 칸 `extraValues`) */
export function initialValues(doc: RecipeV2Doc | undefined): Record<string, string> {
  const out: Record<string, string> = {}
  for (const f of STANDARD_FIELDS) {
    const v = defaultsOf(doc, f.module)?.[f.key]
    if (typeof v === 'number' || typeof v === 'string') out[fieldId(f)] = String(v)
  }
  return { ...out, ...extraValues(doc) }
}

/*
 * ── 레시피 안의 사용자 값 — 작업 차례 · 시약(eric 2026-10-20 「사용자가 입력하는 정하는 부분이 레서피에 잇다며.. 그거 넣어줘봐」) ──
 * 칸 열쇠: 작업 차례 `stage.<단계>.<volume|rpm|seconds>` · 시약 `reagent.<액체 id>.<lot|volume|dead>`.
 *   - 작업 차례 = 액체 스텝 키 앞머리(`s02` …)로 묶은 단계. 부피 = 그 단계 넣기(`dispense`) 스텝의 `volume`, 세이킹 = 흔들기(`shake`) 스텝의
 *     `shake.rpm` · `shake.seconds`. 🔴 반복 횟수는 스텝 수 자체라 여기서 고치지 않는다(보기만).
 *   - 시약 = 편집층 `reagents` 의 키. lot = `reagents.<id>.lot`(선택 · 시작 점검의 lot 만료 · 미등록 검사 재료), 양 = 그 시약 `loadLiquid` 의
 *     `volumeByWell`(칸마다 같을 때만 한 칸 · µL), 남김 양 = `reagents.<id>.dead_volume_ul`(선택 · µL).
 * 🔴 문서에 없는 값은 칸을 세우지 않는다 — 열쇠가 없으면 화면도 입력을 안 그린다.
 */
const STAGE_PREFIX = /^(s\d+)/u

export const stageKey = (stage: string, part: 'volume' | 'rpm' | 'seconds') =>
  `stage.${stage}.${part}`
export const reagentKey = (id: string, part: 'lot' | 'volume' | 'dead') => `reagent.${id}.${part}`

function liquidStepsOf(doc: RecipeV2Doc | undefined): Obj[] {
  const staining = processOf(doc)?.['staining']
  const list = isObj(staining) ? staining['liquid_steps'] : undefined
  return Array.isArray(list) ? list.filter(isObj) : []
}

function dataOf(doc: RecipeV2Doc | undefined): Obj | undefined {
  const da = doc?.['designerApplication']
  const data = isObj(da) ? da['data'] : undefined
  return isObj(data) ? data : undefined
}

function reagentsOf(doc: RecipeV2Doc | undefined): Obj {
  const reagents = dataOf(doc)?.['reagents']
  return isObj(reagents) ? reagents : {}
}

function loadLiquidsOf(doc: RecipeV2Doc | undefined): Obj[] {
  const commands = doc?.['commands']
  return (Array.isArray(commands) ? commands : [])
    .filter(isObj)
    .filter((c) => c['commandType'] === 'loadLiquid' && isObj(c['params']))
}

/** 그 시약의 칸마다 양이 하나로 같으면 그 값(µL) — 다르거나 없으면 `undefined`(칸을 세우지 않는다) */
function uniformVolume(doc: RecipeV2Doc | undefined, id: string): number | undefined {
  const volumes = loadLiquidsOf(doc).flatMap((c) => {
    const params = c['params'] as Obj
    const byWell = params['volumeByWell']
    return params['liquidId'] === id && isObj(byWell) ? Object.values(byWell) : []
  })
  const first = volumes[0]
  return typeof first === 'number' && volumes.every((v) => v === first) ? first : undefined
}

/** 작업 차례 · 시약 칸의 문서 값 */
export function extraValues(doc: RecipeV2Doc | undefined): Record<string, string> {
  const out: Record<string, string> = {}
  const put = (key: string, v: unknown) => {
    if (typeof v === 'number' && !(key in out)) out[key] = String(v)
  }
  for (const step of liquidStepsOf(doc)) {
    const stage = typeof step['key'] === 'string' ? STAGE_PREFIX.exec(step['key'])?.[1] : undefined
    if (stage === undefined) continue
    if (step['operation'] === 'dispense') put(stageKey(stage, 'volume'), step['volume'])
    const shake = step['shake']
    if (step['operation'] === 'shake' && isObj(shake)) {
      put(stageKey(stage, 'rpm'), shake['rpm'])
      put(stageKey(stage, 'seconds'), shake['seconds'])
    }
  }
  for (const [id, entry] of Object.entries(reagentsOf(doc))) {
    if (!isObj(entry)) continue
    out[reagentKey(id, 'lot')] = typeof entry['lot'] === 'string' ? entry['lot'] : ''
    out[reagentKey(id, 'dead')] =
      typeof entry['dead_volume_ul'] === 'number' ? String(entry['dead_volume_ul']) : ''
    const volume = uniformVolume(doc, id)
    if (volume !== undefined) out[reagentKey(id, 'volume')] = String(volume)
  }
  return out
}

/** 칸 글자가 틀렸나 — 장비 기본값은 `badField`, 작업 차례 · 시약 양은 정수 필수, 남김 양은 비우거나 정수, lot 은 자유 */
export function badValue(key: string, text: string | undefined): boolean {
  const field = STANDARD_FIELDS.find((f) => fieldId(f) === key)
  if (field !== undefined) return badField(field, text)
  const value = (text ?? '').trim()
  if (key.endsWith('.lot')) return false
  if (key.startsWith('reagent.') && key.endsWith('.dead'))
    return value !== '' && !/^\d+$/u.test(value)
  return !/^\d+$/u.test(value)
}

/** 틀린 칸 열쇠 — 문서에 있는 칸만 본다 */
export function badKeys(
  doc: RecipeV2Doc | undefined,
  values: Readonly<Record<string, string>>,
): string[] {
  return Object.keys(initialValues(doc)).filter((key) => badValue(key, values[key]))
}

/** 고친 칸 열쇠 — 문서 값과 글자가 다른 것(장비 기본값 · 작업 차례 · 시약 전부) */
export function changedKeys(
  doc: RecipeV2Doc | undefined,
  values: Readonly<Record<string, string>>,
): string[] {
  const base = initialValues(doc)
  return Object.keys(base).filter(
    (key) => values[key] !== undefined && values[key].trim() !== base[key],
  )
}

/**
 * **장비 기본값 ↔ 실제 스텝 값** — 장비가 쓰는 값은 기본값이 아니라 그 시퀀스 스텝마다의 `sequence_params` 다(원심 3회 · UV 9회 · 배출 9회).
 * 기본값을 고치면 **옛 기본값과 같던 스텝 값**도 같이 바꾼다 — 회차마다 일부러 다르게 둔 값은 건드리지 않는다.
 * 🔴 적재 기울기(`load_tilt_angle_deg`)는 스텝 값이 없다(모듈 내장 동작) — 기본값만 바뀐다.
 */
const STEP_LINK: Readonly<Record<string, { sequence: string; param: string }>> = {
  'centrifuge.speed_rpm': { sequence: 'run_centrifugation', param: 'speed_rpm' },
  'centrifuge.seconds': { sequence: 'run_centrifugation', param: 'seconds' },
  'assembler.uv_seconds': { sequence: 'expose_uv', param: 'seconds' },
  'assembler.uv_tilt_angle_deg': { sequence: 'expose_uv', param: 'tilt_angle_deg' },
  'assembler.drain_angle_deg': { sequence: 'invert_drain', param: 'angle_deg' },
}

/** 정수 칸의 글자가 틀렸나 — 비웠거나 숫자가 아니면 */
export function badField(f: StandardField, text: string | undefined): boolean {
  if (f.kind === 'text') return (text ?? '').trim() === ''
  return !/^\d+$/u.test((text ?? '').trim())
}

/** 바뀐 칸들 — 문서 값과 글자가 다른 것만 */
export function changedFields(
  doc: RecipeV2Doc | undefined,
  values: Readonly<Record<string, string>>,
): StandardField[] {
  const base = initialValues(doc)
  return STANDARD_FIELDS.filter((f) => {
    const id = fieldId(f)
    return id in base && values[id] !== undefined && values[id].trim() !== base[id]
  })
}

/**
 * 보낼 문서 — 받은 문서를 통째로 복사하고 **고친 칸과 이름만** 얹는다. 나머지(스텝 · 시약 · 랩웨어 · 명령)는 손대지 않는다.
 * 이름은 문서 안의 `metadata.protocolName` 에도 싣는다(목록의 이름과 문서의 이름이 갈리지 않게).
 */
export function docWithValues(
  doc: RecipeV2Doc,
  values: Readonly<Record<string, string>>,
  name: string,
): RecipeV2Doc {
  const next = structuredClone(doc)
  const process = processOf(next)
  for (const f of changedFields(doc, values)) {
    const mod = process?.[f.module]
    const defaults = isObj(mod) ? mod['defaults'] : undefined
    if (!isObj(defaults)) continue
    const text = (values[fieldId(f)] ?? '').trim()
    const before = defaults[f.key]
    const after = f.kind === 'int' ? Number(text) : text
    defaults[f.key] = after
    // 장비가 실제로 쓰는 스텝 값도 — 옛 기본값과 같던 것만(`STEP_LINK`)
    const link = STEP_LINK[fieldId(f)]
    const steps = isObj(mod) ? mod['module_steps'] : undefined
    if (link === undefined || !Array.isArray(steps)) continue
    for (const step of steps) {
      const params = isObj(step) ? step['params'] : undefined
      const seq = isObj(params) ? params['sequence_params'] : undefined
      if (!isObj(params) || params['sequence'] !== link.sequence || !isObj(seq)) continue
      if (seq[link.param] === before) seq[link.param] = after
    }
  }
  applyExtra(doc, next, values)
  const metadata = next['metadata']
  if (isObj(metadata)) metadata['protocolName'] = name
  return next
}

/** 작업 차례 · 시약 칸을 문서에 얹는다(고친 칸만 · 받은 문서 위에) */
function applyExtra(
  doc: RecipeV2Doc,
  next: RecipeV2Doc,
  values: Readonly<Record<string, string>>,
): void {
  for (const key of changedKeys(doc, values)) {
    const text = (values[key] ?? '').trim()
    const [kind, id, part] = key.split('.') as [string, string, string]
    if (kind === 'stage') {
      for (const step of liquidStepsOf(next)) {
        if (typeof step['key'] !== 'string' || STAGE_PREFIX.exec(step['key'])?.[1] !== id) continue
        if (part === 'volume' && step['operation'] === 'dispense') step['volume'] = Number(text)
        const shake = step['shake']
        if ((part === 'rpm' || part === 'seconds') && step['operation'] === 'shake' && isObj(shake))
          shake[part] = Number(text)
      }
    }
    if (kind === 'reagent') {
      const entry = reagentsOf(next)[id]
      if (part === 'lot' && isObj(entry)) {
        if (text === '') delete entry['lot']
        else entry['lot'] = text
      }
      if (part === 'dead' && isObj(entry)) {
        if (text === '') delete entry['dead_volume_ul']
        else entry['dead_volume_ul'] = Number(text)
      }
      if (part === 'volume')
        for (const c of loadLiquidsOf(next)) {
          const params = c['params'] as Obj
          const byWell = params['volumeByWell']
          if (params['liquidId'] !== id || !isObj(byWell)) continue
          for (const well of Object.keys(byWell)) byWell[well] = Number(text)
        }
    }
  }
}

/*
 * ── 스텝 · 시약 표(eric 2026-10-13 「스텝에서 보면.. 여러개가 있는데.. 컬럼을 분리해줘」) ──────────────────────────────
 * 종전에는 한 줄에 「key · sequence · round」처럼 이어 붙인 요약이었다. 이제 **스텝 객체에 실제로 있는 키**가 열이다 — 그 탭 스텝들의
 * 키 합집합(첫 등장 차례 · 스텝 ID 와 시퀀스/동작이 맨 앞). 응답에 없는 열은 만들지 않는다 · 그 스텝에 없는 칸은 빈 글자(화면이 —).
 * 🔴 장비 스텝(`module_steps`)은 `params` 안의 키를 펴서 열로 올린다. `commandType`(늘 `custom`) · `params.module`(= 그 탭의 장비)은
 *    모든 줄이 같은 값이라 열로 세우지 않는다 — 탭 이름이 이미 말한다. 그 밖의 키는 값이 같아도 다 선다.
 */

/** 표 한 장 — 열(키 · 사람이 읽는 이름 키)과 줄(칸 글자) */
export interface StandardTable {
  columns: readonly { id: string; label: MessageKey | undefined }[]
  rows: readonly { key: string; cells: Readonly<Record<string, string>> }[]
}

/** 열 이름 — 빈 레시피 편집기 · 레시피 상세와 같은 말(`recipeEdit.f.*`)을 쓴다. 모르는 키는 원래 키 이름이 열 머리다(지어내지 않는다) */
const COLUMN_LABEL: Readonly<Record<string, MessageKey>> = {
  key: 'recipeEdit.f.stepId',
  sequence: 'recipeEdit.f.sequence',
  sequence_params: 'recipeStd.col.sequenceParams',
  payload_kind: 'recipeEdit.f.payloadKind',
  round: 'recipeEdit.f.round',
  chambers: 'recipeEdit.f.chambers',
  source: 'recipeEdit.f.source',
  target: 'recipeEdit.f.target',
  body_position: 'recipeEdit.f.bodyPosition',
  note: 'recipeEdit.f.note',
  operation: 'recipeEdit.f.operation',
  wellNames: 'recipeEdit.f.targetWells',
  liquidId: 'column.reagent',
  volume: 'recipeEdit.f.volumeUl',
  liquidClass: 'recipeEdit.f.liquidClass',
  newTip: 'recipeEdit.f.tipPolicy',
  labwareId: 'column.labware',
  to_waste: 'recipeEdit.f.toWaste',
  waste_stream: 'recipeEdit.f.wasteStream',
  shake: 'recipeStd.col.shake',
  id: 'recipeStd.col.reagentNo',
  displayName: 'column.name',
  description: 'recipeStd.col.description',
  layout_detail: 'column.layoutDetail',
  definitionUri: 'recipeStd.col.definitionUri',
  tip_type_index: 'recipeStd.col.tipTypeIndex',
  pick_order: 'recipeStd.col.pickOrder',
  metadata_ext: 'recipeStd.col.metadataExt',
}

/** 맨 앞에 세우는 열 — 스텝 ID · 시퀀스(장비) · 동작(분주) · 번호(시약) */
const LEAD_COLUMNS: readonly string[] = ['id', 'key', 'sequence', 'operation']

/** 열로 안 세우는 키 — 모든 줄이 같은 값이고 탭이 이미 말한다(머리 주석) */
const HIDDEN_KEYS: ReadonlySet<string> = new Set(['commandType', 'module'])

/**
 * 칸 글자 — 서버 값 그대로 읽기 좋게만. 배열은 「, 」 · 객체는 「키 값 · 키 값」 · 참/거짓은 예/아니오. 값이 없으면 빈 글자.
 * 🔴 숫자 · 단위를 지어내지 않는다(µL 같은 단위는 열 머리가 말한다).
 */
export function cellText(value: unknown): string {
  if (value === null || value === undefined) return ''
  if (typeof value === 'string') return value
  if (typeof value === 'number') return String(value)
  if (typeof value === 'boolean') return t(value ? 'value.yes' : 'value.no')
  if (Array.isArray(value)) return value.map((v) => cellText(v)).join(', ')
  if (isObj(value))
    return Object.entries(value)
      .map(([k, v]) => `${k} ${cellText(v)}`)
      .join(' · ')
  return ''
}

function tableOf(
  records: readonly { key: string; fields: Obj }[],
  /** 이 표에서만 다른 열 이름(같은 `id` 가 시약에선 번호 · 랩웨어에선 랩웨어 ID) */
  labels: Readonly<Record<string, MessageKey>> = {},
): StandardTable {
  const seen: string[] = []
  for (const { fields } of records)
    for (const k of Object.keys(fields)) if (!HIDDEN_KEYS.has(k) && !seen.includes(k)) seen.push(k)
  const ordered = [
    ...LEAD_COLUMNS.filter((k) => seen.includes(k)),
    ...seen.filter((k) => !LEAD_COLUMNS.includes(k)),
  ]
  return {
    columns: ordered.map((id) => ({ id, label: labels[id] ?? COLUMN_LABEL[id] })),
    rows: records.map(({ key, fields }) => ({
      key,
      cells: Object.fromEntries(ordered.map((id) => [id, cellText(fields[id])])),
    })),
  }
}

/** 스텝 목록 → 표. 장비 스텝은 `params` 를 펴서 같은 줄에 올린다 */
function stepTable(list: unknown): StandardTable {
  if (!Array.isArray(list)) return { columns: [], rows: [] }
  return tableOf(
    list.filter(isObj).map((step, index) => {
      const { params, ...top } = step
      const fields: Obj = { ...top, ...(isObj(params) ? params : {}) }
      return { key: typeof step['key'] === 'string' ? step['key'] : String(index), fields }
    }),
  )
}

/** 장비 스텝(`module_steps`) 표 */
export function moduleStepTable(
  doc: RecipeV2Doc | undefined,
  module: StandardModule,
): StandardTable {
  const mod = processOf(doc)?.[module]
  return stepTable(isObj(mod) ? mod['module_steps'] : undefined)
}

/** 염색 분주 스텝(`liquid_steps`) 표 */
export function liquidStepTable(doc: RecipeV2Doc | undefined): StandardTable {
  const mod = processOf(doc)?.['staining']
  return stepTable(isObj(mod) ? mod['liquid_steps'] : undefined)
}

/**
 * 랩웨어 탭 표 — 편집층 `labware` 의 자리(키)마다 한 줄 · 그 정의에 실제로 있는 칸이 열(`layout_detail` · `definitionUri` · `tip_type_index` …).
 * ⚠ `metadata_ext` 처럼 큰 객체도 칸이 선다 — 글자는 「키 값 · …」로 펴고 화면이 한 줄로 자른다(전문은 툴팁).
 */
export function labwareTable(
  doc: RecipeV2Doc | undefined,
  /**
   * 랩웨어 ID → 사람이 읽는 이름(서버 요약 `POST /recipes/summary` 의 `labware[].displayName`). 편집층 `labware` 에는 이름 칸이 없다 —
   * 이름은 서버가 정의(`definitionUri`)에서 풀어 요약에만 준다. 넘기면 ID 다음 「이름」 열이 선다(템플릿 › 레시피 판 · 2026-10-13).
   * 안 넘기면 종전 그대로다(이름을 지어내지 않는다)
   */
  names?: Readonly<Record<string, string>>,
): StandardTable {
  const da = doc?.['designerApplication']
  const data = isObj(da) ? da['data'] : undefined
  const labware = isObj(data) ? data['labware'] : undefined
  if (!isObj(labware)) return { columns: [], rows: [] }
  return tableOf(
    Object.entries(labware).map(([id, v]) => {
      const name = names?.[id]
      return {
        key: id,
        fields: {
          id,
          ...(name === undefined ? {} : { displayName: name }),
          ...(isObj(v) ? v : {}),
        },
      }
    }),
    { id: 'recipeStd.col.labwareId' },
  )
}

/** 시약 탭 표 — `liquids` 의 번호(키) · 표시 이름 · 설명(그 밖의 키도 있으면 열로) */
export function liquidTable(doc: RecipeV2Doc | undefined): StandardTable {
  const liquids = doc?.['liquids']
  if (!isObj(liquids)) return { columns: [], rows: [] }
  return tableOf(
    Object.entries(liquids).map(([id, v]) => ({ key: id, fields: { id, ...(isObj(v) ? v : {}) } })),
  )
}
